"use client";

import { useState } from "react";
import { useLang } from "@/contexts/language-context";
import { OPTOUT_COOKIE, isOptedOutClient, setOptOut } from "@/lib/tracking/optOut";
import { readCookie } from "@/lib/marketing/signupAttribution";

/**
 * «Your Privacy Choices» del pie de useredy.com (auditoría legal 2026-10-14).
 *
 * El sitio de marketing carga los pixeles de Meta y TikTok y manda a Meta y
 * Google los identificadores de clic de los anuncios al registrarse. Bajo leyes
 * como la de California eso es «compartir» datos personales, y el visitante
 * tiene que poder excluirse. Usa la MISMA cookie y la misma lógica que el
 * enlace de las tiendas (lib/tracking/optOut.ts); con exclusión no carga
 * ningún pixel (MarketingPixels) ni se guardan los identificadores de clic.
 * Con Global Privacy Control el navegador ya se excluyó: se dice y listo.
 */
export default function MarketingPrivacyChoices({ className }: { className?: string }) {
  const { t } = useLang();
  const [state, setState] = useState<{ optedOut: boolean; byGpc: boolean } | null>(null);

  function open() {
    let optedOut = false;
    let byGpc = false;
    try {
      byGpc = (navigator as { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
      optedOut = isOptedOutClient(document, navigator as { globalPrivacyControl?: boolean });
    } catch {
      // sin cookies: se muestra como no excluido
    }
    setState({ optedOut, byGpc });
  }

  function choose(on: boolean) {
    try {
      setOptOut(document, on, window.location.protocol === "https:");
      if ((readCookie(document.cookie, OPTOUT_COOKIE) === "1") !== on) return;
      // Recargar: un pixel ya cargado no se descarga de otra forma.
      window.location.reload();
    } catch {
      // el navegador bloquea cookies: no hay dónde guardar la elección
    }
  }

  return (
    <>
      <button type="button" onClick={open} className={className}>
        {t("Your Privacy Choices", "Tus opciones de privacidad")}
      </button>
      {state && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="mkt-privacy-choices-title"
          className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0"
          onClick={() => setState(null)}
        >
          <div
            className="w-full max-w-md bg-white rounded-[24px] shadow-2xl p-6 text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="mkt-privacy-choices-title"
              className="text-[20px] leading-tight font-black tracking-tight text-redy-charcoal"
            >
              {t("Your Privacy Choices", "Tus opciones de privacidad")}
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-redy-secondary">
              {t(
                "To measure our ads, useredy.com uses the Meta and TikTok pixels, and when you sign up we send the ad-click identifier to Meta or Google. Under some state laws, like California's, that counts as “sharing” your personal information. You can opt out on this browser.",
                "Para medir nuestros anuncios, useredy.com usa los pixeles de Meta y TikTok, y al registrarte enviamos el identificador del clic del anuncio a Meta o Google. Bajo algunas leyes estatales, como la de California, eso cuenta como “compartir” tu información personal. Puedes excluirte en este navegador."
              )}
            </p>
            <p className="mt-3 text-[14px] font-bold text-redy-charcoal">
              {state.optedOut
                ? t("You're opted out on this browser.", "Estás excluido en este navegador.")
                : t("You're not opted out.", "No estás excluido.")}
            </p>
            {state.byGpc && (
              <p className="mt-2 text-[12px] text-redy-secondary">
                {t(
                  "Your browser sends Global Privacy Control, so you're already opted out.",
                  "Tu navegador envía Global Privacy Control, así que ya estás excluido."
                )}
              </p>
            )}
            <a
              href="/privacy#redy-ad-measurement"
              className="mt-3 inline-block text-[12px] font-bold text-redy-secondary underline underline-offset-2"
            >
              {t("Learn more in our Privacy Policy", "Más detalles en la Política de Privacidad")}
            </a>
            <div className="mt-5 flex flex-col gap-2">
              {!state.byGpc && (
                <button
                  type="button"
                  onClick={() => choose(!state.optedOut)}
                  className="w-full rounded-full bg-redy-charcoal text-white px-4 py-3 text-[14px] font-bold hover:bg-black transition-colors"
                >
                  {state.optedOut
                    ? t("Allow again", "Volver a permitir")
                    : t("Do not sell or share my personal information", "No vender ni compartir mi información personal")}
                </button>
              )}
              <button
                type="button"
                onClick={() => setState(null)}
                className="w-full rounded-full border border-black/10 px-4 py-3 text-[14px] font-bold text-redy-charcoal"
              >
                {t("Close", "Cerrar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
